import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Eye, Loader2, ShieldCheck } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const AUDIENCES = [
  ["child", "For children & young people", "Written in plain, age-appropriate language."],
  ["parent", "For parents & carers", "What we hold about your child and family, and why."],
  ["staff", "For staff", "What we hold about you, including shift-time location."],
  ["gps", "Location & GPS tracking", "When location is collected, who sees it, and how long it is kept."],
];

export default function PrivacyPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/policies/privacy/notices")
      .then((r) => setData(r.data))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, []);

  const anyNotices = data && Object.values(data.groups).some((g) => g.length > 0);

  return (
    <div className="space-y-6 max-w-3xl" data-testid="privacy-page">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Privacy</h1>
        <p className="text-sm text-slate-600 mt-1">
          How Deluxe Travel Training handles personal information — explained separately for children,
          parents/carers, staff and location tracking. Never buried inside general terms.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-600" data-testid="privacy-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : !anyNotices ? (
        <Card data-testid="privacy-pending">
          <CardContent className="py-10 text-center space-y-3">
            <ShieldCheck className="mx-auto h-8 w-8 text-primary" />
            <p className="text-sm text-slate-700 font-medium">Privacy notices are being formally reviewed</p>
            <p className="text-sm text-slate-600 max-w-md mx-auto">
              Our privacy and data-protection notices are organisational templates currently undergoing formal
              management and data-protection review before publication. They will appear here as soon as they are
              published. Questions about your information in the meantime:{" "}
              <a className="text-primary hover:underline" href="mailto:info@deluxehealthcare.co.uk">info@deluxehealthcare.co.uk</a>.
            </p>
            <p className="text-xs text-slate-500">Viewing a privacy notice is never treated as consent.</p>
          </CardContent>
        </Card>
      ) : (
        AUDIENCES.map(([key, label, desc]) => (
          <Card key={key} data-testid={`privacy-group-${key}`}>
            <CardHeader>
              <CardTitle className="text-lg">{label}</CardTitle>
              <p className="text-xs text-slate-500">{desc}</p>
            </CardHeader>
            <CardContent className="space-y-2">
              {(data.groups[key] || []).length === 0 ? (
                <p className="text-sm text-slate-500">Notice pending publication following formal review.</p>
              ) : (
                data.groups[key].map((n) => (
                  <Link key={n.id} to={`/policies/${n.id}`} data-testid={`privacy-notice-${n.id}`}
                    className="flex items-center gap-2 rounded-md border border-border p-3 hover:border-primary/50 hover:bg-slate-50 transition-colors duration-150">
                    <Eye className="h-4 w-4 text-primary shrink-0" />
                    <span className="text-sm font-medium text-slate-900">{n.title}</span>
                    <span className="ml-auto text-xs text-slate-500 font-mono">{n.reference} · v{n.version}</span>
                  </Link>
                ))
              )}
            </CardContent>
          </Card>
        ))
      )}
      {data?.note && <p className="text-xs text-slate-500">{data.note}</p>}
    </div>
  );
}
